import React, { useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import { getErrorMessage } from '../utils/errors';
import {
  ReactFlow,
  Node as FlowNode,
  Edge,
  Controls,
  Background,
  MiniMap,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { workflowService, dashboardService, widgetService, exportImportService, tagService } from '../services';
import {
  Dashboard,
  WorkflowVersion,
  WorkflowVersionDetail,
  Tag,
} from '../types';
import { useToast } from '../hooks/useToast';
import DeleteConfirmationModal from '../components/common/DeleteConfirmationModal';
import CopyModal from '../components/common/CopyModal';
import TagPicker from '../components/common/TagPicker';
import SetupWizardModal, { SetupWizardResult } from '../components/common/SetupWizardModal';
import { widgetRequiresAsset } from '../components/widgets/registry';



// Default edge options — rounded smoothstep paths with a clear arrowhead.
const EDGE_COLOR = '#64748b';        // slate-500: reads as intentional vs flat grey
const EDGE_COLOR_SELECTED = '#f97316';
const defaultEdgeOptions = {
  type: 'smoothstep',
  style: { strokeWidth: 2, stroke: EDGE_COLOR },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: EDGE_COLOR,
    width: 18,
    height: 18,
  },
  animated: false,
};

// Initial nodes for a new workflow
const defaultNodes: FlowNode[] = [
  {
    id: 'start',
    type: 'input',
    data: { label: 'Start' },
    position: { x: 250, y: 0 },
    style: { background: '#10b981', color: 'white', border: 'none' },
  },
  {
    id: 'end',
    type: 'output',
    data: { label: 'End' },
    position: { x: 250, y: 200 },
    style: { background: '#ef4444', color: 'white', border: 'none' },
  },
];

const defaultEdges: Edge[] = [];

const WorkflowBuilder: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [nodes, setNodes, onNodesChange] = useNodesState(defaultNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(defaultEdges);
  const [workflowName, setWorkflowName] = useState('New Workflow');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [selectedNode, setSelectedNode] = useState<FlowNode | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  // Dirty guard: true once the user edits the canvas / fields, cleared on save
  // or (re)load. Only set on real user edits to avoid false-positive warnings.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [availableDashboards, setAvailableDashboards] = useState<Dashboard[]>([]);
  const [showDashboardPicker, setShowDashboardPicker] = useState(false);

  // Versioning state
  const [currentVersion, setCurrentVersion] = useState<number>(1);
  const [versions, setVersions] = useState<WorkflowVersion[]>([]);
  const [activeAssignmentsCount, setActiveAssignmentsCount] = useState<number>(0);
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<WorkflowVersionDetail | null>(null);
  const [, setLoadingVersions] = useState(false);

  // Workflow status state
  const [workflowStatus, setWorkflowStatus] = useState<'draft' | 'published' | 'archived'>('draft');
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  // Asset requirement state
  const [, setRequiresAsset] = useState<boolean>(true);
  const [assetType, setAssetType] = useState<string | null>(null);

  // Expected duration state (in days)
  const [expectedDurationDays, setExpectedDurationDays] = useState<number | undefined>(undefined);

  // Event state
  const [eventCategory, setEventCategory] = useState<string>('');
  const [eventTiming, setEventTiming] = useState<'pre' | 'post' | ''>('');

  // Action modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Tag state
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);

  const loadTags = async () => {
    try {
      const data = await tagService.list();
      setAllTags(data);
    } catch (err) {
      console.error('Failed to load tags', err);
    }
  };

  // Setup wizard for new workflows
  const [showSetupWizard, setShowSetupWizard] = useState(!workflowId);

  // Load available dashboards + tag pool
  useEffect(() => {
    const loadDashboards = async () => {
      try {
        const response = await dashboardService.getDashboards(1, 100);
        setAvailableDashboards(response.data);
      } catch (err) {
        console.error('Failed to load dashboards:', err);
      }
    };
    loadDashboards();
    loadTags();
  }, []);

  // Load workflow if editing an existing one
  useEffect(() => {
    if (workflowId) {
      loadWorkflow(workflowId);
    }
  }, [workflowId]);

  // Warn on browser-level nav (refresh / tab close / hard nav) while there are
  // unsaved edits. In-app Back is guarded separately via the Back button's
  // confirm (beforeunload does not fire for SPA navigate()).
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadWorkflow = async (id: string) => {
    try {
      setLoading(true);
      const workflow = await workflowService.getWorkflow(id);
      setWorkflowName(workflow.name);
      setWorkflowDescription(workflow.description || '');
      setCurrentVersion(workflow.currentVersion || 1);
      setWorkflowStatus(workflow.status);
      setRequiresAsset(workflow.requiresAsset ?? true);
      setAssetType(workflow.assetType || null);
      setExpectedDurationDays(workflow.expectedDurationDays);
      setEventCategory(workflow.eventCategory || '');
      setEventTiming((workflow.eventTiming as 'pre' | 'post') || '');
      setTagIds((workflow.tags || []).map(t => t.id));
      if (workflow.flowData) {
        setNodes(workflow.flowData.nodes || defaultNodes);
        setEdges(workflow.flowData.edges || defaultEdges);
      }
      // Freshly loaded state is clean
      setHasUnsavedChanges(false);
      // Load version info in background
      loadVersionInfo(id);
    } catch (err) {
      console.error('Failed to load workflow:', err);
      toast.error('Failed to load workflow');
      navigate('/workflows');
    } finally {
      setLoading(false);
    }
  };

  // Load version history and active assignments count
  const loadVersionInfo = async (id: string) => {
    try {
      setLoadingVersions(true);
      const [versionsResponse, assignmentsResponse] = await Promise.all([
        workflowService.getWorkflowVersions(id),
        workflowService.getActiveAssignmentsCount(id),
      ]);
      setVersions(versionsResponse.versions);
      setCurrentVersion(versionsResponse.currentVersion);
      setActiveAssignmentsCount(assignmentsResponse.activeAssignmentsCount);
    } catch (err) {
      console.error('Failed to load version info:', err);
    } finally {
      setLoadingVersions(false);
    }
  };

  // Load a specific version to view (read-only)
  const loadVersionSnapshot = async (versionId: string) => {
    if (!workflowId) return;
    try {
      const versionDetail = await workflowService.getWorkflowVersion(workflowId, versionId);
      setViewingVersion(versionDetail);
      // Load the snapshot data into the editor (read-only mode)
      if (versionDetail.snapshotData) {
        setNodes(versionDetail.snapshotData.flowData?.nodes || defaultNodes);
        setEdges(versionDetail.snapshotData.flowData?.edges || defaultEdges);
        setWorkflowName(versionDetail.snapshotData.name);
        setWorkflowDescription(versionDetail.snapshotData.description || '');
      }
      setShowVersionDropdown(false);
    } catch (err) {
      console.error('Failed to load version snapshot:', err);
      toast.error('Failed to load version');
    }
  };

  // Exit version viewing mode and reload current workflow
  const exitVersionView = async () => {
    setViewingVersion(null);
    if (workflowId) {
      await loadWorkflow(workflowId);
    }
  };

  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge = {
        ...params,
        ...defaultEdgeOptions,
      };
      setEdges(eds => addEdge(newEdge, eds));
      setHasUnsavedChanges(true);
    },
    [setEdges],
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: FlowNode) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  const onEdgeClick = useCallback((_: React.MouseEvent, edge: Edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
    setShowStatusMenu(false);
    setShowVersionDropdown(false);
  }, []);

  const deleteSelectedEdge = () => {
    if (selectedEdge) {
      setEdges(eds => eds.filter(e => e.id !== selectedEdge.id));
      setSelectedEdge(null);
      setHasUnsavedChanges(true);
    }
  };

  const addStepNode = () => {
    const newNode: FlowNode = {
      id: `step-${Date.now()}`,
      type: 'default',
      data: { label: `Step ${nodes.length}` },
      position: { x: 250, y: nodes.length * 100 },
      style: { background: '#3b82f6', color: 'white', border: 'none' },
    };
    setNodes(nds => [...nds, newNode]);
    setHasUnsavedChanges(true);
  };

  const deleteSelectedNode = () => {
    if (selectedNode && selectedNode.id !== 'start' && selectedNode.type !== 'output') {
      setNodes(nds => nds.filter(n => n.id !== selectedNode.id));
      setEdges(eds =>
        eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id),
      );
      setSelectedNode(null);
      setHasUnsavedChanges(true);
    }
  };

  // Update node data helper
  const updateNodeData = (updates: Record<string, unknown>) => {
    if (!selectedNode) return;

    const newData = { ...selectedNode.data, ...updates };

    setNodes(nds => nds.map(n => (n.id === selectedNode.id ? { ...n, data: newData } : n)));
    setSelectedNode({
      ...selectedNode,
      data: newData,
    });
    setHasUnsavedChanges(true);
  };

  // Assign dashboard to the selected node
  // Widget configuration is now done in the dashboard builder, not here
  const assignDashboard = (dashboard: Dashboard) => {
    if (!selectedNode) return;
    setShowDashboardPicker(false);

    setNodes(nds =>
      nds.map(n =>
        n.id === selectedNode.id
          ? {
              ...n,
              data: {
                ...n.data,
                label: dashboard.name,
                dashboardId: dashboard.id,
                dashboardName: dashboard.name,
              },
            }
          : n,
      ),
    );
    setSelectedNode({
      ...selectedNode,
      data: {
        ...selectedNode.data,
        label: dashboard.name,
        dashboardId: dashboard.id,
        dashboardName: dashboard.name,
      },
    });
    setHasUnsavedChanges(true);
  };


  // Guard in-app Back navigation when there are unsaved edits. beforeunload
  // does not fire for SPA navigate(), so confirm here before leaving.
  const handleBackClick = () => {
    if (
      hasUnsavedChanges &&
      !window.confirm('You have unsaved changes. Leave without saving?')
    ) {
      return;
    }
    navigate('/workflows');
  };

  // Handle save button click - check if versioning prompt is needed
  const handleSaveClick = async () => {
    // If viewing a past version, don't allow saving
    if (viewingVersion) {
      toast.warning('You are viewing a past version. Exit version view to make changes.');
      return;
    }

    // Validate required fields
    if (!workflowName.trim()) {
      toast.warning('Please enter a workflow name');
      return;
    }
    if (!workflowDescription.trim()) {
      toast.warning('Please enter a workflow description');
      return;
    }
    if (tagIds.length === 0) {
      toast.warning('Please add at least one tag');
      return;
    }
    if (!expectedDurationDays || expectedDurationDays <= 0) {
      toast.warning('Please set an expected duration (days)');
      return;
    }

    // If editing existing workflow with active assignments, show version modal
    if (workflowId && activeAssignmentsCount > 0) {
      setShowVersionModal(true);
      return;
    }

    // Otherwise, save directly
    await saveWorkflow(false);
  };

  // Save workflow with optional version creation
  const saveWorkflow = async (
    createNewVersion: boolean,
    customVersionLabel?: string,
    versionNotes?: string,
  ) => {
    try {
      setSaving(true);
      const flowData = { nodes: nodes as any[], edges: edges as any[] };

      // Auto-derive requiresAsset: scan all dashboard widgets across all nodes.
      // If any widget in any dashboard requires an asset, the workflow does too.
      let derivedRequiresAsset = false;
      for (const node of flowData.nodes) {
        const dashId = node.data?.dashboardId;
        if (!dashId) continue;
        try {
          const dash = await dashboardService.getDashboard(dashId);
          for (const widget of dash.widgets || []) {
            // Look up the template to get the widget type
            const tmpl = await widgetService.getWidgetTemplate(widget.templateId).catch(() => null);
            if (tmpl && widgetRequiresAsset(tmpl.type)) {
              derivedRequiresAsset = true;
              break;
            }
          }
        } catch {
          // Dashboard may not exist yet — skip
        }
        if (derivedRequiresAsset) break;
      }

      if (workflowId) {
        await workflowService.updateWorkflow(workflowId, {
          name: workflowName,
          description: workflowDescription,
          flowData,
          requiresAsset: derivedRequiresAsset,
          assetType,
          expectedDurationDays,
          eventCategory: eventCategory || null,
          eventTiming: eventTiming || null,
          tagIds,
          createNewVersion,
          versionLabel: customVersionLabel,
          versionNotes,
        });
        // Reload version info after save
        await loadVersionInfo(workflowId);
        toast.success(
          createNewVersion
            ? `Workflow updated! Version ${currentVersion} preserved for existing assignments.`
            : 'Workflow updated!',
        );
      } else {
        const newWorkflow = await workflowService.createWorkflow({
          name: workflowName,
          description: workflowDescription,
          flowData,
          requiresAsset: derivedRequiresAsset,
          assetType,
          expectedDurationDays,
          eventCategory: eventCategory || null,
          eventTiming: eventTiming || null,
          tagIds,
        });
        toast.success('Workflow created!');
        navigate(`/workflow-builder/${newWorkflow.id}`, { replace: true });
      }
      setHasUnsavedChanges(false);
      setShowVersionModal(false);
    } catch (err) {
      console.error('Failed to save workflow:', err);
      toast.error('Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  // Handle tag changes — save to backend immediately if editing an existing workflow
  const handleTagsChange = async (newTagIds: string[]) => {
    setTagIds(newTagIds);

    if (workflowId) {
      try {
        await workflowService.updateWorkflow(workflowId, { tagIds: newTagIds });
      } catch (err) {
        console.error('Failed to update tags:', err);
      }
    }
  };

  // Change workflow status (publish, unpublish, archive)
  const changeWorkflowStatus = async (newStatus: 'draft' | 'published' | 'archived') => {
    if (!workflowId) return;

    try {
      setSaving(true);
      await workflowService.updateWorkflow(workflowId, { status: newStatus });
      setWorkflowStatus(newStatus);
      setShowStatusMenu(false);

      const statusMessages = {
        draft: 'Workflow unpublished - now in draft mode',
        published: 'Workflow published - workers can now be assigned',
        archived: 'Workflow archived - no new assignments allowed',
      };
      toast.success(statusMessages[newStatus]);
    } catch (err) {
      console.error('Failed to change workflow status:', err);
      toast.error('Failed to change workflow status');
    } finally {
      setSaving(false);
    }
  };

  // Delete workflow
  const handleDeleteWorkflow = async () => {
    if (!workflowId) return;

    try {
      setIsDeleting(true);
      await workflowService.deleteWorkflow(workflowId);
      setShowDeleteModal(false);
      navigate('/workflows');
    } catch (err: unknown) {
      console.error('Failed to delete workflow:', err);
      toast.error(getErrorMessage(err, 'Failed to delete workflow'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Copy workflow with new name
  const handleCopyWorkflow = async (newName: string) => {
    if (!workflowId) return;

    try {
      setIsCopying(true);
      const newWorkflow = await workflowService.duplicateWorkflow(workflowId, newName);
      setShowCopyModal(false);
      navigate(`/workflow-builder/${newWorkflow.id}`);
    } catch (err: unknown) {
      console.error('Failed to copy workflow:', err);
      toast.error(getErrorMessage(err, 'Failed to copy workflow'));
    } finally {
      setIsCopying(false);
    }
  };

  // Export workflow
  const handleExportWorkflow = async (includeBundle: boolean = false) => {
    if (!workflowId) return;

    try {
      setIsExporting(true);
      setShowExportMenu(false);
      if (includeBundle) {
        await exportImportService.exportWorkflowBundle(workflowId);
      } else {
        await exportImportService.exportWorkflow(workflowId);
      }
    } catch (err: unknown) {
      console.error('Failed to export workflow:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to export workflow');
    } finally {
      setIsExporting(false);
    }
  };

  // Render the properties panel content based on selection
  const renderPropertiesPanel = (): React.ReactNode => {
    if (selectedNode) {
      const isStartOrEnd = selectedNode.id === 'start' || selectedNode.type === 'output';

      if (isStartOrEnd) {
        return (
          <div className="space-y-4">
            <div className="px-3 py-3 bg-neutral-100 rounded-lg">
              <p className="text-sm font-semibold text-neutral-900">{selectedNode.data.label as string}</p>
              <p className="text-xs text-neutral-500 mt-1">
                {selectedNode.id === 'start' ? 'Workflow entry point' : 'Workflow exit point'}
              </p>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          {/* Step label */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Step Name</label>
            <input
              type="text"
              value={selectedNode.data.label as string}
              onChange={e => updateNodeData({ label: e.target.value })}
              className="block w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg"
            />
          </div>

          {/* Dashboard */}
          <div className="pt-3 border-t">
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Dashboard</label>
            {(selectedNode.data.dashboardId as string) ? (
              <button
                onClick={() => setShowDashboardPicker(true)}
                className="w-full p-3 bg-purple-50 border border-purple-200 rounded-lg text-left hover:bg-purple-100 transition-colors group"
              >
                <p className="text-sm font-medium text-purple-900 group-hover:text-purple-700">
                  {selectedNode.data.dashboardName as string}
                </p>
                <p className="text-xs text-purple-500 mt-0.5">Click to change</p>
              </button>
            ) : (
              <button
                onClick={() => setShowDashboardPicker(true)}
                className="w-full px-4 py-3 border-2 border-dashed border-neutral-300 rounded-lg text-sm text-neutral-500 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
              >
                + Select Dashboard
              </button>
            )}
          </div>

          {/* Widget configuration is now done in the Dashboard Builder */}
        </div>
      );
    }

    if (selectedEdge) {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700">From</label>
            <p className="mt-1 text-sm text-neutral-500">
              {(nodes.find(n => n.id === selectedEdge.source)?.data.label as string) ||
                selectedEdge.source}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">To</label>
            <p className="mt-1 text-sm text-neutral-500">
              {(nodes.find(n => n.id === selectedEdge.target)?.data.label as string) ||
                selectedEdge.target}
            </p>
          </div>
          <div className="pt-4 border-t">
            <button
              onClick={deleteSelectedEdge}
              className="w-full px-4 py-2 bg-danger-500 text-white rounded hover:bg-danger-600"
            >
              Delete Connection
            </button>
          </div>
        </div>
      );
    }

    return <p className="text-neutral-500">Select a node or connection to view properties</p>;
  };

  const handleWorkflowSetupComplete = useCallback((result: SetupWizardResult) => {
    setWorkflowName(result.name);
    setWorkflowDescription(result.description);
    setTagIds(result.tagIds);
    if (result.expectedDurationDays) setExpectedDurationDays(result.expectedDurationDays);
    if (result.requiresAsset !== undefined) setRequiresAsset(result.requiresAsset);
    if (result.assetType !== undefined) setAssetType(result.assetType || null);
    if (result.eventCategory) setEventCategory(result.eventCategory);
    if (result.eventTiming) setEventTiming(result.eventTiming);
    setShowSetupWizard(false);
  }, []);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-neutral-500">Loading workflow...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Setup wizard for new workflows */}
      {showSetupWizard && (
        <SetupWizardModal
          type="workflow"
          onComplete={handleWorkflowSetupComplete}
          onCancel={() => navigate('/workflows')}
        />
      )}

      {/* Header */}
      <div className="bg-surface border-b">
        {/* Top row: Back + Name + Version + Actions */}
        <div className="px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <button onClick={handleBackClick} className="h-8 px-3 text-sm text-neutral-700 border border-neutral-300 rounded shadow-sm hover:bg-neutral-50 transition-colors flex-shrink-0">
              ← Back
            </button>
            <input
              type="text"
              value={workflowName}
              onChange={e => { setWorkflowName(e.target.value); setHasUnsavedChanges(true); }}
              disabled={!!viewingVersion}
              className={`h-8 text-sm font-semibold border border-neutral-200 rounded px-2.5 outline-none min-w-0 ${viewingVersion ? 'text-neutral-500 bg-neutral-50' : 'bg-surface'}`}
              placeholder="Workflow name"
            />
            {/* Version badge */}
            {workflowId && (
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setShowVersionDropdown(!showVersionDropdown)}
                  className={`h-8 flex items-center gap-1 px-2.5 rounded text-xs font-medium border ${
                    viewingVersion
                      ? 'bg-warning-50 text-warning-700 border-warning-200'
                      : 'bg-neutral-50 text-neutral-600 border-neutral-200 hover:bg-neutral-100'
                  }`}
                >
                  {viewingVersion ? (
                    <span>Viewing {viewingVersion.versionLabel} (read-only)</span>
                  ) : (
                    <span>v{currentVersion}</span>
                  )}
                  {versions.length > 0 && (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>
                {showVersionDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-surface rounded-lg shadow-lg border z-50">
                    <div className="p-2 border-b">
                      <p className="text-xs text-neutral-500 font-medium">VERSION HISTORY</p>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      <button
                        onClick={() => { if (viewingVersion) exitVersionView(); setShowVersionDropdown(false); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex justify-between items-center ${!viewingVersion ? 'bg-primary-50' : ''}`}
                      >
                        <div>
                          <span>v{currentVersion} (current)</span>
                          {activeAssignmentsCount > 0 && <span className="ml-2 text-xs text-success-600">({activeAssignmentsCount} active)</span>}
                        </div>
                        <span className="text-xs text-primary-600">editing</span>
                      </button>
                      {versions
                        .filter(v => v.versionNumber < currentVersion)
                        .map(version => (
                          <button
                            key={version.id}
                            onClick={() => loadVersionSnapshot(version.id)}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 ${viewingVersion?.id === version.id ? 'bg-warning-50' : ''}`}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <span>{version.versionLabel}</span>
                                {version.activeAssignmentsCount > 0 && <span className="ml-2 text-xs text-success-600">({version.activeAssignmentsCount} active)</span>}
                              </div>
                              <span className="text-xs text-neutral-400">{new Date(version.createdAt).toLocaleDateString()}</span>
                            </div>
                            {version.notes && <p className="text-xs text-neutral-500 mt-0.5 truncate" title={version.notes}>{version.notes}</p>}
                          </button>
                        ))}
                      {versions.filter(v => v.versionNumber < currentVersion).length === 0 && (
                        <p className="px-3 py-2 text-xs text-neutral-400">No previous versions</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Status badge */}
            {workflowId && !viewingVersion && (
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setShowStatusMenu(!showStatusMenu)}
                  className={`h-8 px-2.5 rounded text-xs font-medium flex items-center gap-1.5 border ${
                    workflowStatus === 'published'
                      ? 'bg-success-50 text-success-700 border-success-200 hover:bg-success-100'
                      : workflowStatus === 'archived'
                        ? 'bg-neutral-50 text-neutral-600 border-neutral-200 hover:bg-neutral-100'
                        : 'bg-warning-50 text-warning-700 border-warning-200 hover:bg-warning-100'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    workflowStatus === 'published' ? 'bg-success-500' : workflowStatus === 'archived' ? 'bg-neutral-500' : 'bg-warning-500'
                  }`} />
                  {workflowStatus === 'published' ? 'Published' : workflowStatus === 'archived' ? 'Archived' : 'Draft'}
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showStatusMenu && (
                  <div className="absolute top-full left-0 mt-1 bg-surface border rounded-lg shadow-lg z-20 py-1 min-w-[160px]">
                    {workflowStatus !== 'published' && (
                      <button onClick={() => changeWorkflowStatus('published')} className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-success-500" /> Publish
                        <span className="text-xs text-neutral-400 ml-auto">Allow assignments</span>
                      </button>
                    )}
                    {workflowStatus !== 'draft' && (
                      <button onClick={() => changeWorkflowStatus('draft')} className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-warning-500" /> Unpublish
                        <span className="text-xs text-neutral-400 ml-auto">Set to draft</span>
                      </button>
                    )}
                    {workflowStatus !== 'archived' && (
                      <button onClick={() => changeWorkflowStatus('archived')} className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-neutral-500" /> Archive
                        <span className="text-xs text-neutral-400 ml-auto">Disable workflow</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Active assignments warning */}
            {workflowId && activeAssignmentsCount > 0 && !viewingVersion && (
              <span className="h-8 flex items-center text-xs font-medium text-warning-700 bg-warning-50 border border-warning-200 px-2.5 rounded flex-shrink-0">
                {activeAssignmentsCount} active assignment{activeAssignmentsCount !== 1 ? 's' : ''}
              </span>
            )}

            {/* Asset type indicator */}
            <span className="h-8 flex items-center px-2.5 rounded text-xs font-medium gap-1.5 flex-shrink-0 border bg-neutral-50 text-neutral-500 border-neutral-200">
              <span className="w-1.5 h-1.5 rounded-full bg-neutral-400" />
              {assetType ? assetType.charAt(0).toUpperCase() + assetType.slice(1) : 'Asset: Set at Assignment'}
            </span>

            {/* Event indicator */}
            {eventCategory && (
              <span className="h-8 flex items-center px-2.5 rounded text-xs font-medium gap-1.5 flex-shrink-0 border bg-neutral-50 text-neutral-500 border-neutral-200">
                <span className="w-1.5 h-1.5 rounded-full bg-neutral-400" />
                {eventCategory === 'specific_date'
                  ? 'Event Date: Set at Assignment'
                  : `${eventTiming === 'pre' ? 'Pre' : 'Post'}-${eventCategory.replace(/_/g, ' ')}`}
              </span>
            )}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {viewingVersion ? (
              <button onClick={exitVersionView} className="px-3 py-1.5 text-sm bg-warning-500 text-white rounded-lg hover:bg-warning-600">
                Exit Version View
              </button>
            ) : (
              <>
                <button onClick={addStepNode} className="h-8 px-3 text-xs font-medium text-white bg-primary-600 rounded shadow-sm hover:bg-primary-700 transition-colors">
                  + Add Step
                </button>
                {selectedNode && selectedNode.id !== 'start' && selectedNode.type !== 'output' && (
                  <button onClick={deleteSelectedNode} className="h-8 px-3 text-xs font-medium text-danger-600 border border-danger-300 rounded shadow-sm hover:bg-danger-50 transition-colors">
                    Delete Node
                  </button>
                )}
                {selectedEdge && (
                  <button onClick={deleteSelectedEdge} className="h-8 px-3 text-xs font-medium text-warning-600 border border-warning-300 rounded shadow-sm hover:bg-warning-50 transition-colors">
                    Delete Edge
                  </button>
                )}
                <button
                  onClick={handleSaveClick}
                  disabled={saving}
                  className="h-8 px-4 text-xs font-medium text-white bg-success-600 rounded shadow-sm hover:bg-success-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving...' : workflowId ? 'Update Workflow' : 'Save Workflow'}
                </button>
                {workflowId && (
                  <>
                    <div className="w-px h-5 bg-neutral-200" />
                    <div className="relative">
                      <button
                        onClick={() => setShowExportMenu(!showExportMenu)}
                        disabled={isExporting}
                        className="h-8 px-2.5 text-xs font-medium text-neutral-700 border border-neutral-300 rounded shadow-sm hover:bg-neutral-50 transition-colors flex items-center gap-1"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Export
                      </button>
                      {showExportMenu && (
                        <div className="absolute right-0 mt-1 w-44 bg-surface border rounded-lg shadow-lg z-50">
                          <button onClick={() => handleExportWorkflow(false)} className="w-full px-3 py-2 text-sm text-left hover:bg-neutral-50 rounded-t-lg">Workflow Only</button>
                          <button onClick={() => handleExportWorkflow(true)} className="w-full px-3 py-2 text-sm text-left hover:bg-neutral-50 rounded-b-lg border-t">Full Bundle</button>
                        </div>
                      )}
                    </div>
                    <button onClick={() => setShowCopyModal(true)} disabled={isCopying} className="h-8 px-2.5 text-xs font-medium text-neutral-700 border border-neutral-300 rounded shadow-sm hover:bg-neutral-50 transition-colors">
                      Copy
                    </button>
                    <button onClick={() => setShowDeleteModal(true)} className="h-8 px-2.5 text-xs font-medium text-danger-600 border border-danger-300 rounded shadow-sm hover:bg-danger-50 transition-colors">
                      Delete
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Meta row: description + duration + tags */}
        <div className="px-4 pb-2.5 flex items-center gap-3">
          <input
            type="text"
            value={workflowDescription}
            onChange={e => { setWorkflowDescription(e.target.value); setHasUnsavedChanges(true); }}
            disabled={!!viewingVersion}
            className={`flex-1 text-sm border border-neutral-200 rounded-lg px-3 py-1.5 ${viewingVersion ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-50 focus:bg-surface/20'} outline-none`}
            placeholder="Workflow description"
          />
          {!viewingVersion && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className="text-xs text-neutral-400">Duration:</span>
              <input
                type="number"
                min="1"
                max="365"
                value={expectedDurationDays || ''}
                onChange={e => { setExpectedDurationDays(e.target.value ? parseInt(e.target.value, 10) : undefined); setHasUnsavedChanges(true); }}
                placeholder="—"
                className="w-12 px-1.5 py-1 text-xs border border-neutral-200 rounded-lg text-center"
                title="Expected duration in days"
              />
              <span className="text-xs text-neutral-400">days</span>
            </div>
          )}
          {!viewingVersion && (
            <>
              <div className="w-px h-5 bg-neutral-200 flex-shrink-0" />
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-xs text-neutral-400">Asset:</span>
                <select
                  value={assetType || ''}
                  onChange={e => {
                    const val = e.target.value || null;
                    setAssetType(val);
                    if (!val) { setEventCategory(''); setEventTiming(''); }
                    setHasUnsavedChanges(true);
                  }}
                  className="px-2 py-1 text-xs border border-neutral-200 rounded-lg bg-surface"
                >
                  <option value="">None</option>
                  <option value="equity">Equity</option>
                  <option value="bond" disabled>Bond (soon)</option>
                  <option value="commodity" disabled>Commodity (soon)</option>
                  <option value="currency" disabled>Currency (soon)</option>
                </select>
              </div>
              {assetType && (
                <>
                  <div className="w-px h-5 bg-neutral-200 flex-shrink-0" />
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-xs text-neutral-400">Event:</span>
                    <select
                      value={eventCategory}
                      onChange={e => {
                        const val = e.target.value;
                        setEventCategory(val);
                        if (!val || val === 'specific_date') setEventTiming('');
                        else if (!eventTiming) setEventTiming('pre');
                        setHasUnsavedChanges(true);
                      }}
                      className="px-2 py-1 text-xs border border-neutral-200 rounded-lg bg-surface"
                    >
                      <option value="">None</option>
                      <option value="specific_date">Specific Date</option>
                      <option value="earnings">Earnings</option>
                      <option value="investor_relations_day">IR Day</option>
                      <option value="dividend_ex_date">Dividend Ex-Date</option>
                      <option value="conference">Conference</option>
                      <option value="ipo">IPO</option>
                      <option value="news_alert">News Alert</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  {eventCategory && eventCategory !== 'specific_date' && (
                    <select
                      value={eventTiming}
                      onChange={e => { setEventTiming(e.target.value as 'pre' | 'post'); setHasUnsavedChanges(true); }}
                      className="px-2 py-1 text-xs border border-neutral-200 rounded-lg bg-surface"
                    >
                      <option value="pre">Pre-Event</option>
                      <option value="post">Post-Event</option>
                    </select>
                  )}
                </>
              )}
            </>
          )}
          {!viewingVersion && (
            <div className="flex-shrink-0 min-w-[200px] max-w-md">
              <TagPicker
                value={tagIds}
                onChange={handleTagsChange}
                tags={allTags}
                onTagsChanged={loadTags}
                placeholder="Add tag..."
              />
            </div>
          )}
        </div>
      </div>

      {/* Flow Canvas */}
      <div className="flex-1 flex">
        {/* Properties Panel */}
        <div className="w-64 bg-surface border-r flex flex-col overflow-hidden">
          <div className="p-4 border-b flex-shrink-0">
            <h3 className="text-lg font-semibold">Properties</h3>
          </div>

          <div className="flex-1 overflow-y-auto p-4">{renderPropertiesPanel()}</div>
        </div>

        {/* The global body{zoom:0.8} (index.css) breaks React Flow's coordinate
            math: it measures nodes via getBoundingClientRect(), which returns
            zoom-scaled sizes, so edges anchor ~15px off the handle dots. zoom:1.25
            restores net 1.0 for the canvas subtree (same counter-pattern as .lp).
            width/height:80% × zoom 1.25 = 100% of the flex slot, so it still fills. */}
        <div className="flex-1 relative overflow-hidden">
          <div className="absolute top-0 left-0" style={{ width: '80%', height: '80%', zoom: 1.25 }}>
          <ReactFlow
            nodes={nodes}
            edges={edges.map(edge => {
              const sel = selectedEdge?.id === edge.id;
              return {
                ...edge,
                type: 'smoothstep',
                style: {
                  ...defaultEdgeOptions.style,
                  stroke: sel ? EDGE_COLOR_SELECTED : EDGE_COLOR,
                  strokeWidth: sel ? 2.5 : 2,
                },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: sel ? EDGE_COLOR_SELECTED : EDGE_COLOR,
                  width: 18,
                  height: 18,
                },
              };
            })}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            defaultEdgeOptions={defaultEdgeOptions}
            fitView
          >
            <Controls />
            <MiniMap />
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </ReactFlow>
          </div>
        </div>
      </div>

      {/* Dashboard Picker Modal */}
      {showDashboardPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowDashboardPicker(false)}
          />
          <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Select Dashboard Template</h2>
              <button
                onClick={() => setShowDashboardPicker(false)}
                className="text-neutral-400 hover:text-neutral-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-4 bg-primary-50 border-b border-primary-100">
              <p className="text-sm text-primary-800">
                Dashboard templates define the layout and widgets. Widget data (Q&A questions, Excel
                files, etc.) will be configured in the properties panel after selection.
              </p>
            </div>
            <div className="p-6 overflow-y-auto max-h-96">
              {availableDashboards.length === 0 ? (
                <div className="text-center text-neutral-500 py-8">
                  <p>No dashboards available.</p>
                  <a href="/dashboards" className="text-primary-600 hover:underline mt-2 inline-block">
                    Create dashboards first →
                  </a>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {availableDashboards.map(dashboard => (
                    <button
                      key={dashboard.id}
                      onClick={() => assignDashboard(dashboard)}
                      className={`p-4 border-2 rounded-lg text-left hover:border-purple-500 hover:bg-purple-50 transition-colors ${
                        selectedNode?.data.dashboardId === dashboard.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-neutral-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-neutral-900">{dashboard.name}</p>
                          {dashboard.description && (
                            <p className="text-sm text-neutral-500 mt-1">{dashboard.description}</p>
                          )}
                          <p className="text-xs text-neutral-400 mt-1">
                            {dashboard.widgets.length} widget(s)
                          </p>
                        </div>
                        {selectedNode?.data.dashboardId === dashboard.id && (
                          <span className="text-purple-600 text-sm font-medium">Selected</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Version Modal - shown when saving with active assignments */}
      {showVersionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowVersionModal(false)}
          />
          <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h2 className="text-xl font-semibold">Save Workflow Changes</h2>
            </div>
            <div className="p-6">
              <div className="mb-4 p-3 bg-warning-50 border border-warning-200 rounded-lg">
                <p className="text-sm text-warning-800">
                  This workflow has{' '}
                  <strong>
                    {activeAssignmentsCount} active assignment
                    {activeAssignmentsCount !== 1 ? 's' : ''}
                  </strong>
                  . How would you like to save your changes?
                </p>
              </div>

              {/* Version notes input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Version Notes (optional)
                </label>
                <textarea
                  id="workflow-version-notes-input"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none"
                  rows={2}
                  placeholder="Describe what changed in this version..."
                />
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    const notesInput = document.getElementById(
                      'workflow-version-notes-input',
                    ) as HTMLTextAreaElement;
                    saveWorkflow(true, `v${currentVersion + 1}`, notesInput?.value || undefined);
                  }}
                  className="w-full p-4 border border-neutral-200 rounded-lg text-left hover:border-primary-500 hover:bg-primary-900/20 transition-colors"
                >
                  <p className="font-medium text-neutral-900">
                    Create New Version (v{currentVersion + 1})
                  </p>
                  <p className="text-sm text-neutral-500 mt-1">
                    Current assignments will continue using <strong>v{currentVersion}</strong>.
                    New assignments will use the updated workflow.
                  </p>
                </button>

                <button
                  onClick={() => saveWorkflow(false)}
                  className="w-full p-4 border border-neutral-200 rounded-lg text-left hover:border-warning-500 hover:bg-warning-900/20 transition-colors"
                >
                  <p className="font-medium text-neutral-900">Update In-Place</p>
                  <p className="text-sm text-neutral-500 mt-1">
                    <strong>All active assignments</strong> will see the updated workflow
                    immediately. No version history will be created.
                  </p>
                </button>
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-neutral-50 flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowVersionModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteWorkflow}
        itemName={workflowName}
        itemType="workflow"
        isDeleting={isDeleting}
      />

      {/* Copy Modal */}
      <CopyModal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        onConfirm={handleCopyWorkflow}
        originalName={workflowName}
        itemType="workflow"
        isCopying={isCopying}
      />

    </div>
  );
};

export default WorkflowBuilder;
